import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Pause, Play, Timer } from 'lucide-react';
import api from '@/lib/api';
import { instantCountdown } from '@/lib/eastern';

/**
 * The revision clock, in the drawer's Status tab (Ryan, 2026-10-06: "both"). Rules in backend app/revision_response.py.
 *  - Pause / resume the outstanding revision's 24-hour clock: admin and PMs, with a reason the editor is shown. Paused
 *    time does not count; the deadline moves later by it. The editor's upload starting ends a pause on its own.
 *  - Don't count this round: admin only, with a reason. The round stays listed, out of the Revision Response rate.
 */
const etLabel = (iso) => (iso ? new Date(iso).toLocaleString('en-US', {
  timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
}) + ' ET' : '');
const hrs = (h) => `${(Math.round(h * 10) / 10).toFixed(1)}h`;

function ReasonForm({ testid, placeholder, cta, onSubmit, onCancel, busy }) {
  const [text, setText] = useState('');
  return (
    <div className="space-y-1.5" data-testid={`${testid}-form`}>
      <input value={text} onChange={e => setText(e.target.value)} maxLength={300} placeholder={placeholder} autoFocus
        data-testid={`${testid}-reason`}
        className="ug-field w-full bg-ug-surface border border-ug-border text-zinc-100 rounded-md px-2 py-1.5 text-[11px] outline-none focus:border-ug-gold/40" />
      <div className="flex gap-2">
        <button type="button" disabled={!text.trim() || busy} onClick={() => onSubmit(text.trim())} data-testid={`${testid}-submit`}
          className="tap-auto px-3 py-1 rounded-md bg-gold-leaf text-obsidian text-[10px] font-semibold uppercase tracking-wider disabled:opacity-40">{cta}</button>
        <button type="button" onClick={onCancel} className="tap-auto px-3 py-1 rounded-md border border-ug-border text-[10px] text-slate-400">Cancel</button>
      </div>
    </div>
  );
}

export default function RevisionClockPanel({ project, user, onChanged }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);     // 'pause' | {excuse: requested_at}
  const [busy, setBusy] = useState(false);
  const isAdmin = user.role === 'admin';

  const load = useCallback(() => {
    api.get(`/projects/${project.id}/revision-clock`).then(r => setData(r.data)).catch(() => setData(null));
  }, [project.id]);
  useEffect(() => { load(); }, [load, project.revisions_requested_at, project.revisions_requested]);

  if (!data || (!data.current && !(data.rounds || []).length)) return null;

  const act = async (path, body, ok) => {
    setBusy(true);
    try {
      const r = await api.post(`/projects/${project.id}/${path}`, body || {});
      setData(r.data); setForm(null); toast.success(ok); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not change the revision clock'); }
    finally { setBusy(false); }
  };
  const excuseRow = (requestedAt, excused, by, reason) => (
    excused ? (
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] text-oxidized-teal" data-testid={`revision-excused-${requestedAt}`}>Not counted{reason ? `: ${reason}` : ''}{by ? ` (${by})` : ''}</span>
        {isAdmin && <button type="button" disabled={busy} data-testid={`revision-count-again-${requestedAt}`}
          onClick={() => act('revision-rounds/excuse', { requested_at: requestedAt, excused: false }, 'This round counts again')}
          className="tap-auto text-[10px] text-slate-500 hover:text-zinc-100 underline underline-offset-2">Count it again</button>}
      </div>
    ) : isAdmin && (form?.excuse === requestedAt ? (
      <ReasonForm testid="revision-excuse" placeholder="Why it should not count (the editor sees this)" cta="Don't count it" busy={busy}
        onCancel={() => setForm(null)}
        onSubmit={(reason) => act('revision-rounds/excuse', { requested_at: requestedAt, reason, excused: true }, 'Left out of the editor\'s revision rate')} />
    ) : (
      <button type="button" data-testid={`revision-excuse-${requestedAt}`} onClick={() => setForm({ excuse: requestedAt })}
        className="tap-auto text-[10px] text-slate-500 hover:text-zinc-100 underline underline-offset-2">Don't count this round</button>
    ))
  );

  const cur = data.current;
  const cd = cur && !cur.paused ? instantCountdown(cur.due_at) : null;
  const delivered = (data.rounds || []).filter(r => !cur || r.requested_at !== cur.requested_at).slice().reverse();

  return (
    <div className="p-3 rounded-xl border border-ug-border bg-ug-surface/40 space-y-3" data-testid="revision-clock-panel">
      <div className="flex items-center gap-1.5 font-sans text-[10px] font-semibold uppercase tracking-wider text-ug-gold/80">
        <Timer className="w-3 h-3" /> Revision clock
      </div>
      {cur && (
        <div className="space-y-2" data-testid="revision-clock-current">
          {cur.paused ? (
            <p className="text-[11px] text-zinc-100/90 leading-relaxed" data-testid="revision-clock-paused">
              Paused since {etLabel(cur.paused.since)}{cur.paused.by_name ? ` by ${cur.paused.by_name}` : ''}: {cur.paused.reason}.
              {' '}{hrs(Math.max(0, cur.paused.seconds_left) / 3600)} left when it restarts.
            </p>
          ) : (
            <p className="text-[11px] text-zinc-100/90" data-testid="revision-clock-due">
              Revised draft due {etLabel(cur.due_at)}{cd ? ` (${cd.overdue ? 'overdue' : cd.text.replace('Revision due in ', 'in ')})` : ''}.
            </p>
          )}
          {form === 'pause' ? (
            <ReasonForm testid="revision-pause" placeholder="Why (the editor sees this): e.g. the client is re-sending missing footage" cta="Pause" busy={busy}
              onCancel={() => setForm(null)} onSubmit={(reason) => act('revision-clock/pause', { reason }, 'Revision clock paused; the editor is told')} />
          ) : (
            <div className="flex gap-2 flex-wrap">
              {cur.paused ? (
                <button type="button" disabled={busy} data-testid="revision-clock-resume" onClick={() => act('revision-clock/resume', {}, 'Revision clock restarted; the editor has the new time')}
                  className="tap-auto flex items-center gap-1 px-3 py-1 rounded-md border border-gold-leaf/30 text-[10px] font-semibold uppercase tracking-wider text-ug-gold/80 hover:text-ug-gold">
                  <Play className="w-3 h-3" /> Restart the clock
                </button>
              ) : (
                <button type="button" data-testid="revision-clock-pause" onClick={() => setForm('pause')}
                  className="tap-auto flex items-center gap-1 px-3 py-1 rounded-md border border-ug-border text-[10px] font-semibold uppercase tracking-wider text-slate-400 hover:text-zinc-100">
                  <Pause className="w-3 h-3" /> Pause the clock
                </button>
              )}
            </div>
          )}
          {excuseRow(cur.requested_at, cur.excused, cur.excused_by, cur.excused_reason)}
        </div>
      )}
      {delivered.length > 0 && (
        <div className="space-y-2 pt-1 border-t border-ug-border-subtle" data-testid="revision-rounds">
          {delivered.map(r => (
            <div key={r.requested_at} className="space-y-1" data-testid={`revision-round-${r.requested_at}`}>
              <div className="flex items-center justify-between gap-2 text-[11px]">
                <span className="text-slate-400">Requested {etLabel(r.requested_at)}</span>
                <span className={r.excused ? 'text-slate-500' : r.on_time ? 'text-oxidized-teal' : 'text-ug-status-red'}>
                  {hrs(r.hours)}{r.paused_hours > 0 ? ` (${hrs(r.paused_hours)} paused)` : ''} · {r.on_time ? 'on time' : 'late'}
                </span>
              </div>
              {excuseRow(r.requested_at, r.excused, r.excused_by, r.excused_reason)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
